import {
  getEducationalProgramDetail,
  getProgramResources,
  getAllFilteredPrograms,
  getAllFilteredOwnedPrograms,
  getRedirectedEducationalProgramDetail,
  getAllCustomFilteredPrograms,
  getAllCustomFilteredOwnedPrograms,
  shareEducationalProgram,
  generateEducationalProgramPdf
} from '../../services/EducationalProgramService'
import { isError, isRedirection } from '../../utils/api'
import router, { routes } from '../../router'
import { defaultProgramsPageSize, educationalProgramLanguageStatus, educationalProgramStatus } from '../../utils/constants'

export default {
  state: {
    redirectedProgram: false,
    educationalProgram: null,
    educationalProgramLoading: false,
    educationalProgramError: null,
    educationalProgramResources: [],
    educationalProgramResourcesLoading: false,
    educationalProgramResourcesError: null,
    filteredProgramsLoading: false,
    filteredPrograms: {
      page: 0,
      programs: [],
      totalPages: 0,
      totalCount: 0
    },
    customFilteredProgramsLoading: false,
    customFilteredPrograms: {
      page: 0,
      programs: [],
      totalPages: 0,
      totalCount: 0
    },
    filteredProgramsOwnedLoading: false,
    filteredOwnedPrograms: {
      programs: [],
      totalCount: 0
    },
    customFilteredProgramsOwnedLoading: false,
    customFilteredOwnedPrograms: {
      programs: [],
      totalCount: 0
    },
    navigationOptions: {
      list: true,
      card: false
    }
  },

  mutations: {
    setData(state, { resource, data }) {
      state[resource] = data
    },
    appendData(state, { resource, data }) {
      state[resource] = [...state[resource], ...data]
    },
    setError(state, { resource, error }) {
      state[`${resource}Error`] = error
    },
    setLoading(state, { resource, status }) {
      state[`${resource}Loading`] = status
    },
    setFilteredPrograms(state, status) {
      state.filteredPrograms.programs = [...state.filteredPrograms.programs, ...status.data]
      state.filteredPrograms.page += Math.ceil(status.data.length / 4)
      state.filteredPrograms.totalPages = status.pages
      state.filteredPrograms.totalCount = status.totalCount
    },
    setCustomFilteredPrograms(state, status) {
      state.customFilteredPrograms.programs = [...state.customFilteredPrograms.programs, ...status.data]
      state.customFilteredPrograms.page += Math.ceil(status.data.length / 4)
      state.customFilteredPrograms.totalPages = status.pages
      state.customFilteredPrograms.totalCount = status.totalCount
    },
    clearFilteredPrograms(state) {
      state.filteredPrograms.programs = []
      state.filteredPrograms.page = 0
      state.filteredPrograms.totalPages = 0
    },
    clearCustomFilteredPrograms(state) {
      state.customFilteredPrograms.programs = []
      state.customFilteredPrograms.page = 0
      state.customFilteredPrograms.totalPages = 0
    },
    setFilteredOwnedPrograms(state, educationalPrograms) {
      state.filteredOwnedPrograms.programs = educationalPrograms.data
      state.filteredOwnedPrograms.totalCount = educationalPrograms.totalCount
    },
    setCustomFilteredOwnedPrograms(state, educationalPrograms) {
      state.customFilteredOwnedPrograms.programs = educationalPrograms.data
      state.customFilteredOwnedPrograms.totalCount = educationalPrograms.totalCount
    },
    clearFilteredOwnedPrograms(state) {
      state.filteredOwnedPrograms.programs = []
      state.filteredOwnedPrograms.totalCount = 0
    },
    clearCustomFilteredOwnedPrograms(state) {
      state.customFilteredOwnedPrograms.programs = []
      state.customFilteredOwnedPrograms.totalCount = 0
    },
    setNavigationOptions(state, option) {
      state.navigationOptions[option] = true
    },
    resetNavigationOptions(state) {
      state.navigationOptions.card = false
      state.navigationOptions.list = false
    },
    setRedirectedProgram(state, status) {
      state.redirectedProgram = status
    },
    clearRedirectedProgram(state) {
      state.redirectedProgram = false
    },
    clearEducationalProgramResources(state) {
      state.educationalProgramResources = []
      state.educationalProgramResourcesLoading = false
      state.educationalProgramResourcesError = null
    }
  },

  actions: {
    async getEducationalProgram({ commit }, { id, locale }) {
      commit('clearRedirectedProgram')
      commit('show', { element: 'fullPageLoader' })
      commit('setLoading', { resource: 'educationalProgram', status: true })
      const response = await getEducationalProgramDetail({ id, locale }).catch(e => e.response)
      if (isError(response.status)) {
        router.push({ name: routes.notFound })
        commit('setError', { resource: 'educationalProgram', error: response.data })
      } else if (isRedirection(response.status)) {
        const redirectedResponse = await getRedirectedEducationalProgramDetail(response.data.value).catch(e => e.response)
        commit('setRedirectedProgram', true)
        commit('setData', { resource: 'educationalProgram', data: redirectedResponse.data })
      } else {
        commit('setData', { resource: 'educationalProgram', data: response.data })
      }
      commit('hide', { element: 'fullPageLoader' })
      commit('setLoading', { resource: 'educationalProgram', status: false })
    },
    async hydrateEducationalProgramResources({ commit }, params) {
      commit('setLoading', { resource: 'educationalProgramResources', status: true })
      try {
        const response = await getProgramResources(params)
        if (params.pageNumber === 0) {
          commit('setData', { resource: 'educationalProgramResources', data: [] })
        }
        commit('appendData', { resource: 'educationalProgramResources', data: response.data.data })
      } catch (error) {
        commit('setError', { resource: 'educationalProgramResources', error })
      }
      commit('setLoading', { resource: 'educationalProgramResources', status: false })
    },
    async hydrateFilteredPrograms({ commit }, { pageNumber, pageSize, filters = {} }) {
      commit('setLoading', { resource: 'filteredPrograms', status: true })
      const response = await getAllFilteredPrograms(pageNumber, pageSize, filters).catch(e => e.response)
      if (!isError(response.status)) commit('setFilteredPrograms', response.data)
      commit('setLoading', { resource: 'filteredPrograms', status: false })
    },
    async hydrateCustomFilteredPrograms({ commit }, { pageNumber, pageSize, filters = {} }) {
      commit('setLoading', { resource: 'customFilteredPrograms', status: true })
      const response = await getAllCustomFilteredPrograms(pageNumber, pageSize, filters).catch(e => e.response)
      if (!isError(response.status)) commit('setCustomFilteredPrograms', response.data)
      commit('setLoading', { resource: 'customFilteredPrograms', status: false })
    },
    clearFilteredPrograms({ commit }) {
      commit('clearFilteredPrograms')
    },
    clearCustomFilteredPrograms({ commit }) {
      commit('clearCustomFilteredPrograms')
    },
    clearCustomFilteredOwnedPrograms({ commit }) {
      commit('clearCustomFilteredOwnedPrograms')
    },
    async hydrateFilteredOwnedPrograms({ commit }, { pageNumber, pageSize, filters = {} }) {
      commit('setLoading', { resource: 'filteredOwnedPrograms', status: true })
      const response = await getAllFilteredOwnedPrograms(pageNumber, pageSize, filters).catch(e => e.response)
      if (!isError(response.status)) commit('setFilteredOwnedPrograms', response.data)
      commit('setLoading', { resource: 'filteredOwnedPrograms', status: false })
    },
    async hydrateCustomFilteredOwnedPrograms({ commit }, { pageNumber, pageSize, filters = {} }) {
      commit('setLoading', { resource: 'customFilteredOwnedPrograms', status: true })
      const response = await getAllCustomFilteredOwnedPrograms(pageNumber, pageSize, filters).catch(e => e.response)
      if (!isError(response.status)) commit('setCustomFilteredOwnedPrograms', response.data)
      commit('setLoading', { resource: 'customFilteredOwnedPrograms', status: false })
    },
    clearFilteredOwnedPrograms({ commit }) {
      commit('clearFilteredOwnedPrograms')
    },
    async hydrateCustomEducationalPrograms({ commit, dispatch }) {
      commit('show', { element: 'fullPageLoader' })
      await dispatch('hydrateCustomFilteredOwnedPrograms', { pageNumber: 0, pageSize: defaultProgramsPageSize })
      await dispatch('hydrateCustomFilteredPrograms', {
        pageNumber: 0,
        pageSize: defaultProgramsPageSize,
        filters: {
          purchasedStatus: [educationalProgramStatus.notPurchased],
          languageStatus: [educationalProgramLanguageStatus.comingSoon]
        }
      })
      dispatch('hydrateCustomFilteredPrograms', {
        pageNumber: 1,
        pageSize: defaultProgramsPageSize,
        filters: {
          purchasedStatus: [educationalProgramStatus.notPurchased],
          languageStatus: [educationalProgramLanguageStatus.comingSoon]
        }
      })
      commit('hide', { element: 'fullPageLoader' })
    },
    async shareContent({ commit }, data) {
      commit('show', { element: 'fullPageLoader' })
      commit('hide', { element: 'shareContentModal' })
      await shareEducationalProgram(data)
      commit('hide', { element: 'shareContentModal' })
      commit('hide', { element: 'fullPageLoader' })
      commit('show', {
        element: 'doneModal',
        props: {
          message: data.successMessage,
          background: 'share-resource'
        }
      })
    },
    async generateEducationalProgramPdf({ commit }, { id, locale }) {
      const response = await generateEducationalProgramPdf({ id, locale }).catch(e => e.response)
      const success = !isError(response.status)
      return { success, data: response.data }
    },
    setNavigationOptions({ commit }, { option }) {
      commit('resetNavigationOptions')
      commit('setNavigationOptions', option)
    },
    clearEducationalProgramResources({ commit }) {
      commit('clearEducationalProgramResources')
    }
  },

  getters: {
    getProgramsListForCard(state) {
      return state.filteredOwnedPrograms.programs.map(program => ({
        id: program.id,
        title: program.name,
        author: program.author,
        mainAuthor: program.mainAuthor,
        coAuthors: program.coAuthors,
        base: program.base,
        categoryId: program.categoryId,
        categoryReferenceId: program.categoryReferenceId,
        categoryColor: program.categoryColor,
        duration: program.classTotalQuantity,
        resources: program.resourceTotalQuantity,
        imageURL: program.thumbnail,
        ageRange: program.ageRange,
        secondaryAgeRange: program.secondaryAgeRange,
        novelty: program.novelty,
        activatedLanguages: program.activatedLanguages,
        usedEducationalProgram: program.usedEducationalProgram
      }))
    },
    getCustomProgramsListForCard(state) {
      return state.customFilteredOwnedPrograms.programs.map(program => ({
        id: program.id,
        title: program.name,
        author: program.author,
        mainAuthor: program.mainAuthor,
        coAuthors: program.coAuthors,
        base: program.base,
        categoryId: program.categoryId,
        categoryReferenceId: program.categoryReferenceId,
        categoryColor: program.categoryColor,
        duration: program.classTotalQuantity,
        resources: program.resourceTotalQuantity,
        imageURL: program.thumbnail,
        ageRange: program.ageRange,
        secondaryAgeRange: program.secondaryAgeRange,
        novelty: program.novelty,
        activatedLanguages: program.activatedLanguages,
        usedEducationalProgram: program.usedEducationalProgram
      }))
    }
  }
}
